import React, { useEffect } from 'react';
import {
  ProjectDetailsField,
  ProjectDetailsFieldType,
  ProjectType,
  UpdateProjectDetailsFieldRequest,
  useProjectClosingDateFieldService,
  useProjectCommissionFieldService,
  useProjectDetailsFieldsService
} from 'nekst-api';
import {
  AfterSubmitFunc,
  Dropdown,
  FormTemplate,
  IfFormData,
  TextInput,
  useFormContextRequired,
  useShowContext
} from 'ui-builder';
import { ProjectDetailsFieldTypeFormInput } from './ProjectDetailsFieldTypeDropdown';
import { OpenFormIconButton } from 'features/nekst-widgets';
import { useSetupAreaLabelsHelper } from '../../markets/setupAreaLabelsHelper';
import { IsClosingDateFieldInput } from "./IsClosingDateFieldInput";
import { IsCommissionFieldInput } from "./IsCommissionFieldInput";
import { DatesFieldsInput } from "./DateFieldsInput";

function HiddenStatusDropdown(props: {
  source: string,
  label: string
}) {
  return (
    <Dropdown
      options={[
        {
          value: 0,
          label: 'Show'
        },
        {
          value: 1,
          label: 'Hide'
        }
      ]}
      source={props.source}
      label={props.label}
    />
  );
}


function CommissionSettingsInputs(props: { projectType: ProjectType }) {
  const formContext = useFormContextRequired<ProjectDetailsField>();

  useEffect(() => {
    if (formContext.data?.isHidden) {
      formContext.onChangeCallback({
        isCommissionField: false,
        isClosingDateField: false,
      })
    }
  }, [formContext.data?.isHidden]);

  return (
    <IfFormData conditionFunc={() => [ProjectType.PENDING_DUAL, ProjectType.PENDING_BUYER, ProjectType.PENDING_SELLER].includes(props.projectType)}>
      <IsCommissionFieldInput />
      <IsClosingDateFieldInput />
    </IfFormData>
  )
}

function UpdatePreexistingFieldForm(
  props: {
    projectType: ProjectType,
    data: ProjectDetailsField,
    afterSubmitFunc?: AfterSubmitFunc,
  }
) {
  const service = useProjectDetailsFieldsService();
  const commissionFieldService = useProjectCommissionFieldService();
  const closingDateFieldService = useProjectClosingDateFieldService();

  return (
    <FormTemplate<{
      name: string,
      prompt: string,
      isHidden: number,
      fieldType: ProjectDetailsFieldType,
      _parentField?: { name: string },
      isCommissionField: boolean,
      isClosingDateField: boolean,
      isProjectStartDate: boolean,
      isProjectEndDate: boolean,
    }, ProjectDetailsField>
      title="Update Detail Box"
      initialData={{
        name: props.data.name,
        isHidden: props.data.isHidden ? 1 : 0,
        prompt: props.data.prompt,
        fieldType: props.data.fieldType,
        _parentField: props.data._parentField,
        isCommissionField: props.data.isCommissionField,
        isClosingDateField: props.data.isClosingDateField,
        isProjectStartDate: props.data.isProjectStartDate,
        isProjectEndDate: props.data.isProjectEndDate,
      }}
      afterSubmitFunc={props.afterSubmitFunc}
      submitFormFunc={async (request: {
        name: string,
        prompt: string,
        isHidden: number,
        fieldType: ProjectDetailsFieldType,
        isCommissionField: boolean,
        isClosingDateField: boolean
      }) => {
        const result = await service.updatePredefinedField(props.projectType, props.data.id, {
          name: request.name,
          isHidden: !!request.isHidden,
          prompt: request.prompt,
          fieldType: request.fieldType
        });

        if (request.fieldType === ProjectDetailsFieldType.PRICE) {
          await commissionFieldService.updateIsCommissionField(
            props.projectType,
            props.data.id,
            request.isCommissionField,
          );
        }

        if (request.fieldType === ProjectDetailsFieldType.DATE) {
          await closingDateFieldService.updateIsClosingDateField(
            props.projectType,
            props.data.id,
            request.isClosingDateField,
          );
        }

        return result;
      }}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true,
            minLength: 3
          }
        },
        prompt: {
          type: 'string',
          constraints: {
            maxLength: 3000
          }
        },
        isHidden: {
          type: 'int',
          constraints: {
            required: true
          }
        },
        fieldType: {
          type: 'string',
          constraints: {
            required: true
          }
        }
      }}
    >
      <DatesFieldsInput />
      <TextInput
        source="name"
        label="Name"
        hint={props.data.originalName ? `Original name: ${props.data.originalName}` : undefined}
      />
      <IfFormData conditionFunc={(data) => !!data._parentField}>
        <TextInput
          source="_parentField.name"
          label="Parent Box"
          value={props.data._parentField?.name}
          isDisabled
        />
      </IfFormData>
      <ProjectDetailsFieldTypeFormInput
        source="fieldType"
        label="Box Type"
        value={props.data.fieldType}
      />
      <HiddenStatusDropdown source="isHidden" label="Visibility" />
      <CommissionSettingsInputs projectType={props.projectType} />
    </FormTemplate>
  );
}

function UpdateCustomFieldForm(
  props: {
    projectType: ProjectType,
    data: ProjectDetailsField,
    afterSubmitFunc?: AfterSubmitFunc,
  }
) {
  const service = useProjectDetailsFieldsService();
  const commissionFieldService = useProjectCommissionFieldService();
  const closingDateFieldService = useProjectClosingDateFieldService();

  const labelsHelper = useSetupAreaLabelsHelper();

  return (
    <FormTemplate<UpdateProjectDetailsFieldRequest & {
      isCommissionField: boolean,
      isClosingDateField: boolean
    }, ProjectDetailsField>
      title="Update Detail Box"
      initialData={props.data}
      afterSubmitFunc={props.afterSubmitFunc}
      submitFormFunc={async (request: UpdateProjectDetailsFieldRequest & {
        isCommissionField: boolean,
        isClosingDateField: boolean
      }) => {
        if (props.data.fieldType === ProjectDetailsFieldType.PRICE) {
          await commissionFieldService.updateIsCommissionField(
            props.projectType,
            props.data.id,
            request.isCommissionField
          );
        }

        if (props.data.fieldType === ProjectDetailsFieldType.DATE) {
          await closingDateFieldService.updateIsClosingDateField(
            props.projectType,
            props.data.id,
            request.isClosingDateField
          );
        }

        return await service.updateField(
          props.projectType,
          props.data.id,
          request
        );
      }}
      validationSchema={{
        name: {
          type: 'string',
          constraints: {
            required: true
          }
        },
        alias: {
          type: 'string',
          constraints: {
            required: true,
            regex: {
              value: /^[a-z][A-Z0-9a-z]+$/g,
              message: 'The value must be alphanumeric and start with a letter'
            }
          }
        }
      }}
    >
      <TextInput
        source="name"
        label="Name"
      />
      <TextInput
        source="alias" label="Alias"
        hint="An alphanumeric identifier used as an alias in the API and integrations (e.g., Zapier) for this detail box. Changing this value may affect existing integrations" />
      <ProjectDetailsFieldTypeFormInput
        source="fieldType"
        label={labelsHelper.getFieldTypeLabel()}
      />
      <CommissionSettingsInputs projectType={props.projectType} />
    </FormTemplate>
  );
}

function UpdateProjectDetailsFieldForm(
  props: {
    projectType: ProjectType,
    data: ProjectDetailsField,
    afterSubmitFunc?: AfterSubmitFunc,
  }
) {
  if (props.data.isPreexisting) {
    return (
      <UpdatePreexistingFieldForm
        projectType={props.projectType}
        data={props.data}
        afterSubmitFunc={props.afterSubmitFunc}
      />
    );
  } else {
    return (
      <UpdateCustomFieldForm
        projectType={props.projectType}
        data={props.data}
        afterSubmitFunc={props.afterSubmitFunc}
      />
    );
  }
}

export default function UpdateProjectDetailsFieldButton(
  props: {
    projectType: ProjectType,
  }
) {
  const showContext = useShowContext<ProjectDetailsField>();

  if (showContext?.data) {
    return (
      <OpenFormIconButton
        form={<UpdateProjectDetailsFieldForm
          projectType={props.projectType}
          data={showContext.data}
        />}
        title="Update Field"
      />
    );
  } else {
    return null;
  }

}
